import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckIcon from '@mui/icons-material/Check';
import { Employee, AttendanceStatus } from '../types';
import { salaryService } from '../services/salary.service';

const INK = '#0A0A0A';
const MUTED = '#737373';
const HAIRLINE = '#E5E5E5';
const BG = '#FAFAFA';

const STATUS_LIST: { value: AttendanceStatus; short: string; label: string }[] = [
  { value: AttendanceStatus.PRESENT, short: 'P', label: 'Present' },
  { value: AttendanceStatus.ABSENT, short: 'A', label: 'Absent' },
  { value: AttendanceStatus.HALF_DAY, short: 'HD', label: 'Half day' },
  { value: AttendanceStatus.LEAVE, short: 'L', label: 'Leave' },
  { value: AttendanceStatus.HOLIDAY, short: 'H', label: 'Holiday' },
];

/**
 * Returns YYYY-MM-DD in the user's LOCAL timezone.
 *
 * Critical bug fix: previously we used `new Date().toISOString().slice(0, 10)`
 * which converts to UTC. In IST (UTC+5:30), the local "today" reads back as
 * yesterday's UTC date — meaning every attendance date got shifted 1 day
 * earlier. Bulk-marking June would write May 31 → June 29 instead of June 1
 * → June 30, silently losing one day from the month.
 */
const formatLocalISO = (dt: Date): string => {
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const todayISO = (): string => formatLocalISO(new Date());

const shiftDate = (iso: string, delta: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return formatLocalISO(new Date(y, m - 1, d + delta));
};

const friendlyDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Returns YYYY-MM-DD list of all dates within a given YYYY-MM month, optionally
// excluding Saturdays and Sundays. Dates are formatted in LOCAL time — see
// formatLocalISO comment for why this matters.
function datesInMonth(yyyymm: string, excludeWeekends: boolean): string[] {
  const [y, m] = yyyymm.split('-').map(Number);
  const out: string[] = [];
  // m is 1-indexed; loop until the month rolls over
  for (let day = 1; day <= 31; day++) {
    const dt = new Date(y, m - 1, day);
    if (dt.getMonth() !== m - 1) break; // ran past end of month
    const dow = dt.getDay(); // 0 = Sun, 6 = Sat
    if (excludeWeekends && (dow === 0 || dow === 6)) continue;
    out.push(formatLocalISO(dt));
  }
  return out;
}

function currentYM(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

export function AttendanceTracking() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [date, setDate] = useState<string>(todayISO());
  // employeeId → status (null = unmarked)
  const [marks, setMarks] = useState<Record<string, AttendanceStatus | null>>({});
  const [savedMarks, setSavedMarks] = useState<Record<string, AttendanceStatus | null>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Bulk-month dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState<string>(currentYM());
  const [bulkStatus, setBulkStatus] = useState<AttendanceStatus>(AttendanceStatus.PRESENT);
  const [bulkExcludeWeekends, setBulkExcludeWeekends] = useState(true);
  const [bulkSelectedEmployees, setBulkSelectedEmployees] = useState<Set<string>>(new Set());

  // Load employees once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { employees } = await salaryService.getEmployees(true);
        if (mounted) setEmployees(employees);
      } catch (err: any) {
        if (mounted) setNotice({ severity: 'error', message: err?.message || 'Failed to load employees' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load attendance whenever the date changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await salaryService.getAttendanceByDate(date);
        if (!mounted) return;
        const next: Record<string, AttendanceStatus | null> = {};
        for (const a of res.attendance) next[a.employeeId] = a.status;
        setMarks(next);
        setSavedMarks(next);
      } catch {
        if (mounted) {
          setMarks({});
          setSavedMarks({});
        }
      }
    })();
    return () => { mounted = false; };
  }, [date]);

  const counts = useMemo(() => {
    let marked = 0;
    let present = 0;
    let absent = 0;
    let leave = 0;
    for (const e of employees) {
      const s = marks[e.employeeId];
      if (s) marked += 1;
      if (s === AttendanceStatus.PRESENT) present += 1;
      else if (s === AttendanceStatus.ABSENT) absent += 1;
      else if (s === AttendanceStatus.LEAVE) leave += 1;
    }
    return { marked, present, absent, leave, total: employees.length };
  }, [employees, marks]);

  const pendingChanges = useMemo(() => {
    let n = 0;
    for (const e of employees) {
      if (marks[e.employeeId] !== savedMarks[e.employeeId]) n += 1;
    }
    return n;
  }, [employees, marks, savedMarks]);

  const setMark = (employeeId: string, status: AttendanceStatus) => {
    setMarks((prev) => ({
      ...prev,
      [employeeId]: prev[employeeId] === status ? null : status,
    }));
  };

  const markAllUnmarked = (status: AttendanceStatus) => {
    setMarks((prev) => {
      const next = { ...prev };
      for (const e of employees) {
        if (!next[e.employeeId]) next[e.employeeId] = status;
      }
      return next;
    });
  };

  const discard = () => setMarks(savedMarks);

  const openBulk = () => {
    // Default to all employees selected
    setBulkSelectedEmployees(new Set(employees.map((e) => e.employeeId)));
    setBulkMonth(currentYM());
    setBulkStatus(AttendanceStatus.PRESENT);
    setBulkExcludeWeekends(true);
    setBulkOpen(true);
  };

  const toggleBulkEmployee = (id: string) => {
    setBulkSelectedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitBulk = async () => {
    const dates = datesInMonth(bulkMonth, bulkExcludeWeekends);
    const ids = Array.from(bulkSelectedEmployees);
    if (dates.length === 0 || ids.length === 0) {
      setNotice({ severity: 'info', message: 'Pick at least one employee and one date' });
      return;
    }
    setBusy(true);
    try {
      const res = await salaryService.bulkCreateMultiDateAttendance({
        dates,
        records: ids.map((employeeId) => ({ employeeId, status: bulkStatus })),
      });
      const created = res.results?.created?.length ?? 0;
      const skipped = res.results?.skipped?.length ?? 0;
      setNotice({
        severity: skipped > 0 ? 'info' : 'success',
        message: `Saved ${created} entries${skipped > 0 ? ` · skipped ${skipped} already marked` : ''}`,
      });
      setBulkOpen(false);
      // If we just modified the currently-viewed date, refresh marks
      if (dates.includes(date)) {
        const res2 = await salaryService.getAttendanceByDate(date);
        const next: Record<string, AttendanceStatus | null> = {};
        for (const a of res2.attendance) next[a.employeeId] = a.status;
        setMarks(next);
        setSavedMarks(next);
      }
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Bulk save failed' });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const records = employees
      .filter((e) => marks[e.employeeId] && marks[e.employeeId] !== savedMarks[e.employeeId])
      .map((e) => ({ employeeId: e.employeeId, status: marks[e.employeeId] as AttendanceStatus }));

    if (records.length === 0) {
      setNotice({ severity: 'info', message: 'No changes to save' });
      return;
    }

    setBusy(true);
    try {
      await salaryService.bulkCreateAttendance({ date, records });
      setSavedMarks(marks);
      setNotice({ severity: 'success', message: `Saved ${records.length} record${records.length === 1 ? '' : 's'}` });
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Save failed' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size={32} sx={{ color: INK }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto', pb: 12 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.4, color: INK }}>
            Attendance
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            {friendlyDate(date)} · {counts.marked} of {counts.total} marked
          </Typography>
        </Box>

        {/* Date controls */}
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => setDate(shiftDate(date, -1))} disabled={busy}>
            <NavigateBeforeIcon />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value || todayISO())}
            sx={{ '& input': { py: 0.75, fontFamily: 'inherit', color: INK, fontSize: 13 } }}
            InputProps={{ sx: { borderRadius: 1 } }}
          />
          <IconButton size="small" onClick={() => setDate(shiftDate(date, 1))} disabled={busy}>
            <NavigateNextIcon />
          </IconButton>
          <Button
            size="small"
            onClick={() => setDate(todayISO())}
            variant="outlined"
            sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none', fontWeight: 500 }}
          >
            Today
          </Button>
          <Button
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
            onClick={openBulk}
            variant="outlined"
            sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none', fontWeight: 500 }}
          >
            Mark whole month
          </Button>
        </Stack>
      </Stack>

      {/* Single inline action — only what HR will actually use */}
      {counts.marked < counts.total && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 1,
            bgcolor: BG,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {counts.total - counts.marked} unmarked. Mark the exceptions below, then bulk-mark the rest.
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => markAllUnmarked(AttendanceStatus.PRESENT)}
              sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none', fontWeight: 500 }}
            >
              Mark rest Present
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => markAllUnmarked(AttendanceStatus.HOLIDAY)}
              sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none', fontWeight: 500 }}
            >
              Mark rest Holiday
            </Button>
          </Stack>
        </Box>
      )}

      {/* Employee list */}
      <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: '#FFFFFF', overflow: 'hidden' }}>
        {employees.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>
            No active employees. Add an employee first.
          </Box>
        )}
        {employees.map((e, idx) => {
          const current = marks[e.employeeId];
          return (
            <Stack
              key={e.employeeId}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: idx < employees.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                '&:hover': { bgcolor: BG },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500, color: INK, fontSize: 14 }}>{e.name}</Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }}>
                  {e.designation || '—'}{e.employeeCode ? ` · ${e.employeeCode}` : ''}
                </Typography>
              </Box>

              <Stack direction="row" gap={0.5}>
                {STATUS_LIST.map((s) => {
                  const active = current === s.value;
                  return (
                    <Tooltip key={s.value} title={s.label} arrow>
                      <Button
                        onClick={() => setMark(e.employeeId, s.value)}
                        size="small"
                        sx={{
                          minWidth: 36,
                          height: 28,
                          px: 1,
                          borderRadius: 0.75,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          border: `1px solid ${active ? INK : HAIRLINE}`,
                          bgcolor: active ? INK : '#FFFFFF',
                          color: active ? '#FFFFFF' : MUTED,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: INK,
                            bgcolor: active ? '#262626' : BG,
                          },
                        }}
                      >
                        {s.short}
                      </Button>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Stack>
          );
        })}
      </Box>

      {/* Sticky save bar — only when there are pending changes */}
      {pendingChanges > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            bgcolor: INK,
            color: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {pendingChanges} change{pendingChanges === 1 ? '' : 's'} pending
          </Typography>
          <Button
            size="small"
            onClick={discard}
            sx={{ color: '#D4D4D8', textTransform: 'none', fontWeight: 500, '&:hover': { color: '#FFFFFF' } }}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
            onClick={save}
            disabled={busy}
            sx={{
              bgcolor: '#FFFFFF',
              color: INK,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#F4F4F5' },
            }}
          >
            {busy ? 'Saving' : 'Save'}
          </Button>
        </Box>
      )}

      {/* Bulk month dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: INK, pb: 0.5 }}>
          Mark whole month
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
            Apply one status to every working day of the month for the selected employees.
            Existing records won't be overwritten — only blank days get marked.
          </Typography>

          <Stack gap={1.5}>
            <Stack direction="row" gap={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  label="Month"
                  value={bulkMonth.split('-')[1]}
                  onChange={(e) => setBulkMonth(`${bulkMonth.split('-')[0]}-${e.target.value}`)}
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <MenuItem key={idx} value={String(idx + 1).padStart(2, '0')}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Year"
                size="small"
                type="number"
                value={bulkMonth.split('-')[0]}
                onChange={(e) => setBulkMonth(`${e.target.value || '2026'}-${bulkMonth.split('-')[1]}`)}
                inputProps={{ min: 2020, max: 2100 }}
                sx={{ width: 110 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value as AttendanceStatus)}
                >
                  {STATUS_LIST.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkExcludeWeekends}
                  onChange={(e) => setBulkExcludeWeekends(e.target.checked)}
                  size="small"
                  sx={{ '&.Mui-checked': { color: INK } }}
                />
              }
              label={<Typography sx={{ fontSize: 13, color: INK }}>Exclude Saturdays and Sundays</Typography>}
            />

            <Box sx={{ p: 1.5, border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: BG }}>
              <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
                {datesInMonth(bulkMonth, bulkExcludeWeekends).length} day{datesInMonth(bulkMonth, bulkExcludeWeekends).length === 1 ? '' : 's'} ×{' '}
                {bulkSelectedEmployees.size} employee{bulkSelectedEmployees.size === 1 ? '' : 's'} = up to{' '}
                {datesInMonth(bulkMonth, bulkExcludeWeekends).length * bulkSelectedEmployees.size} records
              </Typography>
            </Box>

            {/* Employee selection */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: INK }}>
                  Employees ({bulkSelectedEmployees.size} of {employees.length})
                </Typography>
                <Stack direction="row" gap={0.5}>
                  <Button
                    size="small"
                    onClick={() => setBulkSelectedEmployees(new Set(employees.map((e) => e.employeeId)))}
                    sx={{ fontSize: 11, color: INK, textTransform: 'none', minWidth: 0 }}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setBulkSelectedEmployees(new Set())}
                    sx={{ fontSize: 11, color: MUTED, textTransform: 'none', minWidth: 0 }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${HAIRLINE}`, borderRadius: 1 }}>
                {employees.map((e, idx) => (
                  <Stack
                    key={e.employeeId}
                    direction="row"
                    alignItems="center"
                    onClick={() => toggleBulkEmployee(e.employeeId)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      borderBottom: idx < employees.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                      '&:hover': { bgcolor: BG },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={bulkSelectedEmployees.has(e.employeeId)}
                      onChange={() => toggleBulkEmployee(e.employeeId)}
                      sx={{ p: 0.5, '&.Mui-checked': { color: INK } }}
                    />
                    <Typography sx={{ fontSize: 13, color: INK, ml: 1 }}>{e.name}</Typography>
                  </Stack>
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitBulk}
            disabled={busy || bulkSelectedEmployees.size === 0}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
          >
            {busy ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={3500}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default AttendanceTracking;
